import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearTokens,
  getPermissions,
  hasTokens,
  saveTokens,
} from 'common/utils/providerHelpers/tokensHelper';
import routes from 'common/constants/path';
import resources from 'common/constants/resources';
import { commonRoles } from 'common/constants/enums';
import { profileActionTypes } from 'common/ducks/actionTypes';
import createAuthProvider from 'common/utils/providerHelpers/createAuthProvider';
import { errorMessages, urls } from './constants';
import dataProvider from '../dataProvider';

const authProvider = createAuthProvider(
  { saveTokens, clearTokens, hasTokens, getPermissions },
  urls,
  errorMessages
);

const defaultAuthState = {
  ...authProvider,
  loading: false,
  isEcgTech: false,
  isLoggedIn: null,
  profileData: null,
  rootRedirect: null,
  currentTenant: null,
  termsAccepted: null,
  currentUserId: null,
  currentUserRole: null,
  currentTenantId: null,
  currentPhysicianId: null,
  isTenantAuthorized: false,
  getIsChildTenant: () => false,
  updateCurrentTenant: () => undefined,
};

export const AuthContext = createContext(defaultAuthState);

const getProfileData = (state) => state.profile?.data;

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [termsAccepted, setTerms] = useState(null);
  const [isLoggedIn, setLoggedIn] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const profileData = useSelector(getProfileData);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);

      const user = await authProvider.fetchUser();

      setLoading(false);

      return user;
    } catch (e) {
      console.error(e?.body?.message || 'User not found');
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const data = await authProvider.checkAuth();

      // eslint-disable-next-line no-console
      console.log('Check user', data);

      if (data?.TenantId) {
        // eslint-disable-next-line no-console
        console.log('Start getting organization with ID', data?.TenantId);
        const { data: tenant } = await dataProvider.getOne(
          `${resources.tenant.index}`,
          { id: data.TenantId }
        );

        setCurrentTenant(tenant);
      }

      setLoggedIn(!!data);
      setUserData(data);
    } catch (e) {
      setUserData(null);
      setLoggedIn(false);
      console.error('User not found', e);
    }
  }, []);

  const validateAccount = useCallback(
    async ({ params, onError }) => {
      try {
        setLoading(true);

        const result = await authProvider.validateAccount(params);
        setTerms(result?.termsAccepted);

        if (result?.token) {
          saveTokens(result.token);
          checkAuth();
        }

        setLoading(false);
      } catch (e) {
        setLoading(false);

        if (onError) {
          onError(e);
        }
      }
    },
    [checkAuth]
  );

  const acceptTermsOfUse = useCallback(
    async (params) => {
      setLoading(true);

      const result = await authProvider.acceptTermsOfUse(params);
      setTerms(true);
      setLoading(false);

      if (result?.token) {
        saveTokens(result.token);
        checkAuth();
      }

      return result;
    },
    [checkAuth]
  );

  const login = useCallback(
    async ({ params, onError }) => {
      try {
        setLoading(true);

        await authProvider.login(params);
        const user = await fetchUser();
        checkAuth();

        setLoading(false);

        return user;
      } catch (e) {
        if (onError) {
          onError(e);
          setLoading(false);
        }

        return null;
      }
    },
    [checkAuth, fetchUser]
  );

  const logout = useCallback(
    async (params) => {
      setLoading(true);

      const result = await authProvider.logout(params);
      checkAuth();
      setTerms(null);
      setLoading(false);
      setCurrentTenant(null);

      return result;
    },
    [checkAuth]
  );

  const forgotPassword = useCallback(async ({ params, onSuccess, onError }) => {
    try {
      setLoading(true);

      const result = await authProvider.forgotPassword(params);

      if (onSuccess) {
        onSuccess(result);
      }
      setLoading(false);
    } catch (e) {
      if (onError) {
        onError(e);
      }

      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(
    async ({ params, onSuccess, onError }) => {
      try {
        setLoading(true);

        const result = await authProvider.resetPassword(params);

        dispatch({ type: profileActionTypes, payload: { data: result } });

        if (onSuccess) {
          onSuccess(result);
        }
        setLoading(false);
      } catch (e) {
        if (onError) {
          onError(e);
        }

        setLoading(false);
      }
    },
    [dispatch]
  );

  const clearLoginState = useCallback(() => {
    setTerms(null);
    setUserData(null);
  }, []);

  const getIsChildTenant = useCallback(
    (tenantId) => tenantId && tenantId !== userData?.TenantId,
    [userData]
  );

  const updateCurrentTenant = useCallback(
    (tenant) => {
      if (tenant && tenant?.id === userData?.TenantId) {
        setCurrentTenant(tenant);
      }
    },
    [userData]
  );

  const rootRedirect = useMemo(() => {
    if (!currentTenant) {
      return routes.private.dashboard;
    }

    return currentTenant.enableDashboard
      ? routes.private.dashboard
      : routes.private.triages.escalated;
  }, [currentTenant]);

  const value = useMemo(
    () => ({
      ...authProvider,
      validateAccount,
      acceptTermsOfUse,
      login,
      logout,
      loading,
      fetchUser,
      isLoggedIn,
      profileData,
      rootRedirect,
      currentTenant,
      termsAccepted,
      resetPassword,
      forgotPassword,
      clearLoginState,
      getIsChildTenant,
      updateCurrentTenant,
      isEcgTech: userData?.Role === commonRoles.ecgTech,
      currentUserId: userData?.Id,
      currentUserRole: userData?.Role,
      currentTenantId: userData?.TenantId,
      currentPhysicianId: userData?.PhysicianId,
      isTenantAuthorized: Boolean(userData?.TenantId),
    }),
    [
      login,
      logout,
      loading,
      userData,
      fetchUser,
      isLoggedIn,
      profileData,
      rootRedirect,
      currentTenant,
      termsAccepted,
      resetPassword,
      forgotPassword,
      validateAccount,
      clearLoginState,
      acceptTermsOfUse,
      getIsChildTenant,
      updateCurrentTenant,
    ]
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
